import React, { useEffect, useState } from 'react';

import {
  dealBidderTypeRenamingMap,
  DealType,
  getNonThirdPartyDeals,
  getSamsungDeals,
  getUnrulyDeals,
  IDeal,
} from '~/src/dash/api/deals';
import { EBool } from '~/src/dash/api/types/bool';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';

interface IDealSelectProps {
  name: string;
  onChange: (elem: { target: { value: IDeal[] } }) => void;
  publisherID: string;
  value: IDeal[];
  bidderType: string;
  disabled?: boolean;
  isDealPrivateAuctionEnabled: boolean;
}

export const DealTargetingMultiSelect = function ({
  onChange,
  disabled = false,
  name,
  value = [],
  publisherID,
  bidderType,
  isDealPrivateAuctionEnabled,
}: IDealSelectProps) {
  const [dealMenuList, setDealMenuList] = useState<IMenuList>({});
  const [deals, setDeals] = useState<IDeal[]>([]);

  async function loadDeals() {
    try {
      let getDealPromise;
      if (bidderType === 'rhythmone') {
        getDealPromise = getUnrulyDeals(publisherID);
      } else if (bidderType === 'samsungssp') {
        getDealPromise = getSamsungDeals(publisherID);
      } else {
        getDealPromise = getNonThirdPartyDeals(publisherID);
      }
      const deals: IDeal[] = await getDealPromise.then((deals: IDeal[]) => deals);
      setDeals(deals);
      updateMenuList(deals);
    } catch (e) {
      setDealMenuList({});
    }
  }

  function handleMenuSelectionDone(menuList: IMenuList) {
    const dealIDStrings = Object.values(menuList)
      .filter((src: IListItem) => {
        return src.selected;
      })
      .map((src: IListItem) => src.ID);

    const selectedSegments = deals.filter((deal) => dealIDStrings.includes(deal.ID.toString()));

    onChange({
      target: {
        name,
        value: selectedSegments,
      },
    } as any);
  }

  function updateMenuList(deals: IDeal[]) {
    // Filter the deal list so it only contains deals that have the same bidder type as the selected bidder.
    deals = filterDealsByBidderType(deals, bidderType);
    // Filter the deal list based on the bidder's Private Auction property.
    deals = filterDealsForPrivateAuction(deals, isDealPrivateAuctionEnabled);
    // Filter out inactive deals
    deals = deals.filter((deal) => deal.Active === EBool.EBool_TRUE);
    // Update the deal list.
    setDealMenuList(buildDealMenuList(deals, value));
  }

  function filterDealsByBidderType(deals: IDeal[], bidderType: string): IDeal[] {
    return deals.filter(
      (deal) =>
        deal.BidderType !== '' &&
        (deal.BidderType === bidderType ||
          dealBidderTypeRenamingMap[deal.BidderType] === bidderType),
    );
  }

  useEffect(() => {
    loadDeals();
  }, [publisherID]);

  useEffect(() => {
    updateMenuList(deals);
  }, [bidderType, isDealPrivateAuctionEnabled]);

  return (
    <MaterialSearchDropdownMulti
      getValueFromSearchElement={(src: IListItem) => src.title}
      selectAll={true}
      menuTitle='Deal'
      list={dealMenuList}
      disabled={disabled}
      onChange={setDealMenuList}
      handleMenuAcceptClick={handleMenuSelectionDone}
      isNoneSelectedAllSelected={false}
      helperText='Choose which deal to bid on.'
    />
  );
};

function filterDealsForPrivateAuction(
  deals: IDeal[],
  isDealPrivateAuctionEnabled: boolean,
): IDeal[] {
  // If the bidder has Private Auction enabled, then only display the deals that are programmatic guaranteed or private auction.
  if (isDealPrivateAuctionEnabled) {
    return deals.filter(
      (deal: IDeal) =>
        deal.DealType === DealType.PrivateAuction ||
        deal.DealType === DealType.ProgrammaticGuaranteed,
    );
  }

  // If the bidder doesn't have Private Auction enabled, then only display the deals that aren't programmatic guaranteed or private auction.
  return deals.filter(
    (deal: IDeal) =>
      deal.DealType !== DealType.PrivateAuction &&
      deal.DealType !== DealType.ProgrammaticGuaranteed,
  );
}

function buildDealMenuList(deals: IDeal[], selectedDeals: IDeal[]): IMenuList {
  const list = {} as IMenuList;
  const selectedIDs = selectedDeals.map((deal) => deal.ID);

  deals.forEach((deal) => {
    list[deal.ID] = {
      title: deal.Name,
      ID: deal.ID.toString(),
      selected: selectedIDs.includes(deal.ID),
    } as IListItem;
  });
  return list;
}
